import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Modal } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { pickPageName, pickPortalName } from '../../../../utils/dataPortalCache';
import { DataPortalGroupType } from '../graphql/queries';

interface Props {
    open: boolean;
    groups: DataPortalGroupType[];
    /** 可选来源门户，已按 sortOrder 排好序；列表按门户分段展示 */
    portalCodes: string[];
    onCancel: () => void;
    /** 确认时回传新的分组 id 顺序（从上到下 = 从前到后），交给 reorderDataPortalGroups 一次整序 */
    onConfirm: (orderedIds: string[]) => void;
}

const ITEM_TYPE = 'DP_GROUP_REORDER_ROW';

interface Row {
    id: string;
    name: string;
    portalCode: string;
}

/**
 * 分组拖拽排序弹窗。
 *
 * 几十个分组用一个个 ↑↓ 调太慢，这里一次拖到位。列表从上到下即最终展示顺序（越靠上越靠前），
 * 确认时把整串 id 交给后端 `reorderDataPortalGroups` 一次重写 sort_order，天生幂等。
 *
 * 用项目既有的 react-dnd（非新依赖），hover 即时交换本地顺序，落库只在点确认时发生。
 *
 * **按门户分段，且禁止跨门户拖拽**：分组绑定唯一门户，二级栏只比较同门户内分组的先后，
 * 跨门户混排出来的顺序没有实际含义；而重新打开弹窗时行会按门户重新分段，
 * 跨门户拖过的结果看起来像「拖了但没生效」，比直接拦住更让人困惑。
 */
const GroupReorderModal: React.FC<Props> = ({ open, groups, portalCodes, onCancel, onConfirm }) => {
    const { t, i18n } = useTranslation();
    const { modal } = App.useApp();
    const [rows, setRows] = useState<Row[]>([]);

    // 打开时以当前展示顺序初始化（groups 已是 sort_order 降序），并按门户分段。
    // 门户顺序用后端给的 portalCodes；配置里没有的门户（历史数据）兜底排在末尾，
    // 不能直接丢掉——丢了会让这些分组在提交的整序列表里缺席，sortOrder 被清成 0
    const orderedGroups = useMemo(() => {
        const rank = (code: string) => {
            const index = portalCodes.indexOf(code);
            return index < 0 ? portalCodes.length : index;
        };
        return [...groups].sort((a, b) => rank(a.portalCode) - rank(b.portalCode));
    }, [groups, portalCodes]);

    useEffect(() => {
        if (open) {
            setRows(
                orderedGroups.map((g) => ({
                    id: g.id,
                    name: pickPageName(g, i18n.language) || g.id,
                    portalCode: g.portalCode,
                })),
            );
        }
    }, [open, orderedGroups, i18n.language]);

    const moveRow = (from: number, to: number) => {
        setRows((prev) => {
            if (from === to || from < 0 || to < 0 || to >= prev.length) return prev;
            // 跨门户拖拽的**主闸在 ReorderRow 的 hover 里**（那里能在改 item.index 之前拦住）。
            // 这里留一道纵深防御：本函数是唯一的顺序写入口，任何新调用方都过这一关
            if (prev[from].portalCode !== prev[to].portalCode) return prev;
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    return (
        <Modal
            open={open}
            title={t('central.dataPortalConfig.actions.reorderGroups')}
            onCancel={onCancel}
            onOk={() =>
                modal.confirm({
                    title: t('central.dataPortalConfig.confirmDialog.title'),
                    content: t('central.dataPortalConfig.confirmDialog.reorder'),
                    okText: t('components.button.confirm') as string,
                    cancelText: t('components.button.cancel') as string,
                    onOk: () => onConfirm(rows.map((r) => r.id)),
                })
            }
            okText={t('components.button.confirm') as string}
            cancelText={t('components.button.cancel') as string}
            destroyOnClose
        >
            <div className="mb-2 text-xs text-gray-400">{t('central.dataPortalConfig.tips.reorderGroups')}</div>
            <DndProvider backend={HTML5Backend}>
                <div className="flex flex-col gap-1">
                    {rows.map((row, index) => (
                        <React.Fragment key={row.id}>
                            {/* 门户分段标题：仅在该门户第一行之前出现 */}
                            {(index === 0 || rows[index - 1].portalCode !== row.portalCode) && (
                                <div className="text-xs font-medium text-gray-500 mt-2 first:mt-0">
                                    {pickPortalName(row.portalCode, t)}
                                </div>
                            )}
                            <ReorderRow index={index} row={row} moveRow={moveRow} />
                        </React.Fragment>
                    ))}
                </div>
            </DndProvider>
        </Modal>
    );
};

interface RowProps {
    row: Row;
    index: number;
    moveRow: (from: number, to: number) => void;
}

/** 拖拽载荷。带上 portalCode 是为了在 hover 里判跨门户——那里拿不到最新的 rows */
interface DragItem {
    index: number;
    portalCode: string;
}

const ReorderRow: React.FC<RowProps> = ({ row, index, moveRow }) => {
    const ref = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop<DragItem>({
        accept: ITEM_TYPE,
        hover(item, monitor) {
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            // 跨门户在这里就早退，**必须早于下面的 item.index 更新**：
            // 否则「行没动、但拖拽项以为自己已经到了 hoverIndex」，接着往下悬停
            // 就会拿这个错的 dragIndex 去交换两个与本次拖拽无关的行。
            // 拖拽项自带 portalCode 而不是回查 rows，是因为 hover 里拿不到最新的 rows。
            if (item.portalCode !== row.portalCode) return;

            // 只有当指针越过目标行的「中线」才交换 —— 否则每次悬停都无条件 moveRow，
            // 会在两行之间来回抖动、卡片乱跳（甚至看起来落到最下方）。这是 react-dnd
            // 官方 sortable 示例的关键一步，之前漏了。
            const rect = ref.current.getBoundingClientRect();
            const middleY = (rect.bottom - rect.top) / 2;
            const offset = monitor.getClientOffset();
            if (!offset) return;
            const pointerY = offset.y - rect.top;
            // 向下拖：指针未过中线不动；向上拖：指针未过中线不动
            if (dragIndex < hoverIndex && pointerY < middleY) return;
            if (dragIndex > hoverIndex && pointerY > middleY) return;

            moveRow(dragIndex, hoverIndex);
            // 直接改 item.index，避免同一次拖拽里对同一目标反复触发交换
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
        type: ITEM_TYPE,
        item: { index, portalCode: row.portalCode },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    drag(drop(ref));

    return (
        <div
            ref={ref}
            className={`flex items-center gap-2 px-3 py-2 border border-gray-200 rounded bg-white cursor-move ${
                isDragging ? 'opacity-40' : ''
            }`}
        >
            <HolderOutlined className="text-gray-400" />
            <span className="text-gray-400 w-6 flex-none">{index + 1}</span>
            <span className="truncate">{row.name}</span>
        </div>
    );
};

export default GroupReorderModal;
